import * as React from "react";
import { formatDate } from "helper/date-time";
import { getFullAddress, numberFormat } from "helper/format-data";
import { ParkingMeter } from "services/web/api-parking-meter.type";
import useToggle from "hooks/use-toggle";
import { ParkingMeterModalState } from "../parking-payment.const";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

type ParkingMeterDetailProps = {
  parkingMeter: ParkingMeter;
  setParkingModalState: React.Dispatch<React.SetStateAction<keyof typeof ParkingMeterModalState>>;
  handleRemoveParkingMeter: (parkingMeterId: string) => void;
};

export const ParkingMeterDetail = ({
  parkingMeter,
  setParkingModalState,
  handleRemoveParkingMeter,
}: ParkingMeterDetailProps) => {
  const [openConfirm, setOpenConfirm] = useToggle();

  const removeParkingMeter = () => {
    if (parkingMeter.id) {
      handleRemoveParkingMeter(parkingMeter.id);
    }
  };

  const address = getFullAddress(parkingMeter.address);

  return (
    <div className="transition-opacity absolute h-auto top-36 bg-primary p-3 right-6 mt-2 mb-3 rounded-md shadow-lg z-5 w-80">
      <div>
        <div className="flex justify-between w-full items-center py-2">
          <span className="text-sm text-secondary">Name</span>
          <span className="text-sm text-secondary">{parkingMeter?.name}</span>
        </div>
        <div className="flex justify-between w-full items-center py-2 gap-2">
          <span className="text-sm text-secondary">Address</span>
          <span className="text-sm text-secondary">{address}</span>
        </div>
        <div className="flex justify-between w-full items-center py-2">
          <span className="text-sm text-secondary">Status</span>
          <span className="text-sm text-secondary">{parkingMeter?.status}</span>
        </div>
        <div className="flex justify-between w-full items-center py-2">
          <span className="text-sm text-secondary">External ID</span>
          <span className="text-sm text-secondary">{parkingMeter?.externalId}</span>
        </div>
        <div className="flex justify-between w-full items-center py-2">
          <span className="text-sm text-secondary">Operator</span>
          <span className="text-sm text-secondary">{parkingMeter?.operator}</span>
        </div>
        <div className="flex justify-between w-full items-center py-2">
          <span className="text-sm text-secondary">Brand</span>
          <span className="text-sm text-secondary">{parkingMeter?.brand}</span>
        </div>
        <div className="flex justify-between w-full items-center py-2">
          <span className="text-sm text-secondary">Model</span>
          <span className="text-sm text-secondary">{parkingMeter?.model}</span>
        </div>
        <div className="flex justify-between w-full items-center py-2">
          <span className="text-sm text-secondary">Zone</span>
          <span className="text-sm text-secondary">{parkingMeter?.zone}</span>
        </div>
        <div className="flex justify-between w-full items-center py-2">
          <span className="text-sm text-secondary">Battery Level</span>
          <span className="text-sm text-secondary">{numberFormat(parkingMeter?.batteryLevel)}</span>
        </div>
        {parkingMeter?.lastMaintenanceDate && (
          <div className="flex justify-between w-full items-center py-2">
            <span className="text-sm text-secondary">Last Maintenance Date</span>
            <span className="text-sm text-secondary">{formatDate(parkingMeter?.lastMaintenanceDate)}</span>
          </div>
        )}
      </div>
      <div className="flex justify-between mt-4">
        <Button onClick={setOpenConfirm} variant="destructive">
          Delete
        </Button>
        <Button onClick={() => setParkingModalState(ParkingMeterModalState.SHOW)} variant="secondary">
          Edit
        </Button>
      </div>
      <AlertDialog open={openConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete a parking meter</AlertDialogTitle>
            <AlertDialogDescription>Are you sure that you want to delete this parking meter?</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={setOpenConfirm}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={removeParkingMeter}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
